import createClient from 'openapi-fetch'
import type { paths } from '../types/integrations/signicat/operations'
import { getMiddleware } from './utils'
import type {
  SignicatCreateDocumentCollectionRequest,
  SignicatCreateSigningSessionRequest,
  SignicatListSigningSessionsQuery,
  SignicatMerchantSignRequest,
  SignicatPatchSigningSessionRequest,
  SignicatUpdateDocumentMetadataRequest,
  SignicatUploadDocumentContentType,
} from '../types/integrations/signicat/common'

const baseUrl = 'http://integration-proxy-api/signicat'
const signicatClient = createClient<paths>({
  baseUrl,
})
signicatClient.use(getMiddleware())

export async function signicatUploadDocument(body: any, contentType: SignicatUploadDocumentContentType) {
  const response = await fetch(`${baseUrl}/documents`, {
    method: 'POST',
    headers: {
      'Content-Type': contentType,
    },
    body,
  })

  const { ok, status, statusText } = response
  if (!ok) {
    const error = await response.text()
    throw new Error(`Failed to upload document. Response: ${status} ${statusText} ${error}`)
  }

  return await response.json()
}

export async function signicatGetDocument(documentId: string) {
  const response = await fetch(`${baseUrl}/documents/${documentId}`)

  const { ok, status, statusText } = response
  if (!ok) {
    const error = await response.text()
    throw new Error(`Failed to get document. Response: ${status} ${statusText} ${error}`)
  }

  return await response.arrayBuffer()
}

export async function signicatDeleteDocument(documentId: string) {
  return await signicatClient.DELETE('/documents/{documentId}', {
    params: { path: { documentId } },
  })
}

export async function signicatGetDocumentInfo(documentId: string) {
  const { data } = await signicatClient.GET('/documents/{documentId}/metadata', {
    params: { path: { documentId } },
  })
  return data
}

export async function signicatUpdateDocumentMetadata(documentId: string, body: SignicatUpdateDocumentMetadataRequest) {
  const { data } = await signicatClient.PATCH('/documents/{documentId}/metadata', {
    params: { path: { documentId } },
    body,
  })
  return data
}

export async function signicatCreateDocumentCollection(body: SignicatCreateDocumentCollectionRequest) {
  const { data } = await signicatClient.POST('/document-collections', { body })
  return data
}

export async function signicatGetDocumentCollection(documentCollectionId: string) {
  const { data } = await signicatClient.GET('/document-collections/{documentCollectionId}', {
    params: { path: { documentCollectionId } },
  })
  return data
}

export async function signicatDeleteDocumentCollection(documentCollectionId: string) {
  return await signicatClient.DELETE('/document-collections/{documentCollectionId}', {
    params: { path: { documentCollectionId } },
  })
}

export async function signicatListSigningSessions(query?: SignicatListSigningSessionsQuery) {
  const { data } = await signicatClient.GET('/signing-sessions', {
    params: { query },
  })
  return data
}

export async function signicatCreateSigningSession(body: SignicatCreateSigningSessionRequest) {
  const { data } = await signicatClient.POST('/signing-sessions', { body })
  return data
}

export async function signicatGetSigningSession(sessionId: string) {
  const { data } = await signicatClient.GET('/signing-sessions/{sessionId}', {
    params: { path: { sessionId } },
  })
  return data
}

export async function signicatDeleteSigningSession(sessionId: string) {
  return await signicatClient.DELETE('/signing-sessions/{sessionId}', {
    params: { path: { sessionId } },
  })
}

export async function signicatPatchSigningSession(sessionId: string, body: SignicatPatchSigningSessionRequest) {
  return await signicatClient.PATCH('/signing-sessions/{sessionId}', {
    params: { path: { sessionId } },
    body,
  })
}

export async function signicatMerchantSign(body: SignicatMerchantSignRequest) {
  const { data } = await signicatClient.POST('/merchant-signing', { body })
  return data
}
